/**
 * SafetyAttestationDialog.jsx — OH&S / OSHA fit-for-duty checklist (Feb 2026).
 *
 * Shown when a worker (or team representative) clicks Start on a job.
 * The backend blocks the start until every item is acknowledged AND the
 * worker explicitly confirms via the primary button. Bypass options:
 *   • Refuse work — closes the dialog, no start, optional reason toast.
 *
 * Network contract:
 *   GET  /api/company/safety-attestation → { enabled, items, version }
 *   PUT  /api/jobs/{id}/start            → { latitude, longitude,
 *                                            safety_attestation_accepted,
 *                                            safety_attestation_items,
 *                                            safety_attestation_version }
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { ShieldCheck, AlertTriangle, Loader2 } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

export default function SafetyAttestationDialog({
  open,
  onClose,
  onAccept,            // (template) => Promise<void>  — caller does the actual /start
  jobTitle,
  workerName,           // who is attesting (may differ from current user on team jobs)
  isTeamJob = false,
  teamName = null,
}) {
  const [template, setTemplate] = useState(null);   // { enabled, items, version }
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState({});       // index → bool
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const token = localStorage.getItem('token');
    axios
      .get(`${API}/company/safety-attestation`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { if (!cancelled) { setTemplate(data); setChecked({}); } })
      .catch(() => { if (!cancelled) setTemplate({ enabled: true, items: [], version: 1 }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const items = template?.items || [];
  const allChecked = useMemo(
    () => items.length > 0 && items.every((_, i) => !!checked[i]),
    [items, checked],
  );

  const handleAccept = async () => {
    if (!allChecked || submitting) return;
    setSubmitting(true);
    try {
      await onAccept({
        accepted: true,
        items,
        version: template?.version || 1,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefuse = () => {
    onClose?.({ refused: true });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.({ refused: false }); }}>
      <DialogContent
        className="max-w-[95vw] sm:max-w-xl max-h-[92vh] flex flex-col"
        data-testid="safety-attestation-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-500" />
            Safety attestation — before you start
          </DialogTitle>
          <DialogDescription>
            {isTeamJob && teamName ? (
              <>
                You&apos;re starting <span className="font-semibold">{jobTitle}</span> on behalf of team{' '}
                <span className="font-semibold">{teamName}</span>. Please confirm the following on
                behalf of yourself before the team begins work.
              </>
            ) : (
              <>You&apos;re about to start <span className="font-semibold">{jobTitle || 'this job'}</span>. Please confirm each item below before work begins.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate-400 text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading checklist…
            </div>
          ) : items.length === 0 ? (
            <div className="flex items-start gap-3 p-3 rounded-md bg-amber-500/10 border border-amber-500/30">
              <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-200">
                No safety items configured. Your administrator should set the OH&amp;S / OSHA
                attestation under Settings → Compliance.
              </p>
            </div>
          ) : (
            items.map((text, i) => (
              <label
                key={i}
                htmlFor={`safety-item-${i}`}
                className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                  checked[i]
                    ? 'border-emerald-500/40 bg-emerald-500/5'
                    : 'border-slate-700 hover:border-slate-500'
                }`}
                data-testid={`safety-item-${i}`}
              >
                <Checkbox
                  id={`safety-item-${i}`}
                  checked={!!checked[i]}
                  onCheckedChange={(v) => setChecked((s) => ({ ...s, [i]: !!v }))}
                  className="mt-0.5"
                  data-testid={`safety-checkbox-${i}`}
                />
                <span className="text-sm leading-relaxed" style={{ color: '#E2E8F0' }}>{text}</span>
              </label>
            ))
          )}
        </div>

        <div className="rounded-md bg-slate-900/60 border border-slate-700 p-3 text-[12px] leading-relaxed text-slate-400">
          By clicking <span className="text-emerald-300 font-medium">I attest &amp; start work</span>{' '}
          you are providing a legal acknowledgment that the statements above are true to the best
          of your knowledge. A timestamped record will be attached to this job and visible to your
          supervisor.
        </div>

        <DialogFooter className="gap-2 flex-shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={handleRefuse}
            disabled={submitting}
            data-testid="safety-refuse-btn"
          >
            I&apos;m not safe to work — cancel
          </Button>
          <Button
            type="button"
            onClick={handleAccept}
            disabled={!allChecked || submitting || loading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="safety-accept-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
            {submitting ? 'Recording…' : 'I attest & start work'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
